/**
 * Utilities for syncing SearchPageV2 URL path with API canonical/cityUrl
 * without stripping amenity segments (e.g. /climate-control-storage-units/).
 *
 * Map-based search: when the user drags the map we push the current pathname
 * unchanged and only update the query (location=lat,lng). We do not build or replace location in the canonical URL.
 */
import { traceError } from "utils/traceContext";
import { LANDING_PAGE_QUERY } from "utils/search/buildLandingQuery";

const SELF_STORAGE_PREFIX = "/self-storage/";

// Derive vehicle category slugs from LANDING_PAGE_QUERY to avoid duplication.
// These represent vehicle type filters, not path destinations, so they should not
// be added via canonical sync when filtering on a CLP.
const VEHICLE_CATEGORY_SLUGS = new Set(
	Object.entries(LANDING_PAGE_QUERY)
		.filter(([, query]) => query.type === "vehicle")
		.map(([slug]) => slug)
);

/**
 * Strip vehicle category slugs from a path when they shouldn't be there.
 * Vehicle categories should only be in the path for dedicated VLP pages,
 * not when filtering results on a CLP.
 * @param {string} path - Normalized path (no trailing slash)
 * @returns {string} Normalized path without vehicle category slug (no trailing slash)
 */
export function stripVehicleCategoryFromPath(path) {
	if (!path) return path;
	const segments = path.split("/").filter(Boolean);
	if (segments.length > 0) {
		const lastSegment = segments[segments.length - 1];
		if (VEHICLE_CATEGORY_SLUGS.has(lastSegment)) {
			// Return normalized path (no trailing slash) to match normalizePath convention
			const result = `/${segments.slice(0, -1).join("/")}`;
			return result || "/";
		}
	}
	return path;
}

/**
 * Slugify a display city name the same way city URL slugs are built
 * (lowercase, spaces/punctuation collapsed to single hyphens). Accented
 * letters are normalized to their base ASCII letter (e.g. "Cañon" -> "canon")
 * rather than collapsed to a hyphen, and apostrophes are stripped rather than
 * hyphenated (e.g. "O'Fallon" -> "ofallon") to match the city/state slug
 * charset actually used on this site: server/render.js's facility-path
 * patterns only allow "'" in the trailing facility-name segment, never in the
 * state/city segments, so a real city slug never contains one.
 * @param {string} name - Display name, e.g. "Mechanicsburg"
 * @returns {string} slug, e.g. "mechanicsburg"
 */
function slugifyCityName(name) {
	if (!name || typeof name !== "string") return "";
	return name
		.normalize("NFD")
		.replace(/\p{M}/gu, "")
		.replace(/'/g, "")
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

/**
 * Normalize path: ensure leading slash, strip trailing slash.
 * @param {string} path - Path with or without leading/trailing slash
 * @returns {string}
 */
export function normalizePath(path) {
	if (!path || typeof path !== "string") return "";
	let trimmed = path.trim();
	while (trimmed.endsWith("/")) trimmed = trimmed.slice(0, -1);
	return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

/**
 * Build a corrected CLP city path from the gateway's resolved location.city
 * for this request, rather than the possibly-unresolved cityUrl/canonical.
 * Used to recover a corrected city path when the raw URL slug was misspelled
 * and the API fell back to a state-only cityUrl/canonical.
 * @param {Object} opts
 * @param {string} opts.stateUrl - State-only path, e.g. /self-storage/pennsylvania/
 * @param {string} opts.city - Resolved display city name, e.g. "Mechanicsburg"
 * @returns {string|null} Corrected city path with trailing slash, or null
 */
export function buildCityUrlFromLocation({ stateUrl, city }) {
	const stateSlug = normalizePath(stateUrl)
		.replace(/^\/self-storage\//, "")
		.replace(/\/$/, "");
	const citySlug = slugifyCityName(city);
	if (!stateSlug || !citySlug) return null;
	return `${SELF_STORAGE_PREFIX}${stateSlug}/${citySlug}/`;
}

/**
 * Extract pathname from canonical (full URL or path-only).
 * @param {string} canonical - Full URL or path like /self-storage/state/city/
 * @returns {string|null} Normalized path or null if invalid
 */
export function getPathFromCanonical(canonical) {
	if (!canonical || typeof canonical !== "string") return null;
	try {
		// Full URL
		if (
			canonical.startsWith("http://") ||
			canonical.startsWith("https://")
		) {
			const path = new URL(canonical).pathname;
			return path.startsWith(SELF_STORAGE_PREFIX)
				? normalizePath(path)
				: null;
		}
		// Path-only
		const p = canonical.startsWith("/") ? canonical : `/${canonical}`;
		return p.startsWith(SELF_STORAGE_PREFIX) ? normalizePath(p) : null;
	} catch (error) {
		traceError(`getPathFromCanonical failed for: ${canonical}`, error);
		return null;
	}
}

/**
 * Number of path segments (e.g. /a/b/c -> 3).
 * @param {string} path - Normalized path
 * @returns {number}
 */
export function getPathSegmentCount(path) {
	return path ? path.split("/").filter(Boolean).length : 0;
}

/**
 * State/city key from path (e.g. /self-storage/california/mountain-view -> "california/mountain-view").
 * Used to detect same location so we do not overwrite URL on browser back (stale API state).
 * Comparison is case-insensitive so casing fixes still sync.
 * @param {string} pathNorm - Normalized path
 * @returns {string|null} "state/city" lowercased, or null if path has fewer than 3 segments
 */
function getStateCityKey(pathNorm) {
	const segments = pathNorm ? pathNorm.split("/").filter(Boolean) : [];
	return segments.length >= 3
		? `${segments[1]}/${segments[2]}`.toLowerCase()
		: null;
}

/**
 * Decide the target path to sync to: prefer canonical (includes amenity when present),
 * fall back to cityUrl. Returns null if we should not replace (e.g. preserve amenity
 * when current path has it but API returned city-only).
 *
 * @param {Object} opts
 * @param {string} opts.pathname - Current location pathname
 * @param {string} opts.cityUrl - City path from API (e.g. /self-storage/california/mountain-view/)
 * @param {string} [opts.canonical] - Full canonical URL or path from API
 * @param {string} [opts.city] - Resolved display city name from location.city, used to recover
 * a corrected city path when cityUrl/canonical fell back to a state-only URL (misspelled city slug).
 * @param {string} [opts.fullState] - The gateway's resolved location.fullState (e.g. "North Carolina").
 * When present it must match the pathname's state before recovery runs: a misspelled slug the
 * geocoder resolved to a same-named city in a different state is not a correction for this URL.
 * Skipped when absent (nothing to verify against).
 * @param {boolean} [opts.isPathnameStale] - True when pathname changed without a matching
 * cityUrl/canonical/city update (see useMisspelledCityRedirect.js's freshness tracking). A
 * pathname-only client-side navigation (browser back/forward between two SPA-visited pages, or
 * the low-inventory redirectTo push) leaves Redux holding the previous page's result while
 * pathname has already moved - recovery must not treat that stale data as the correction for
 * the new pathname.
 * @param {boolean} [opts.isCoordinateSearch] - True when the current query is a map-drag
 * coordinate search (?location=<lat>, <lng>), not a city-slug lookup. A state-only cityUrl
 * fallback here means the dragged-to position is unresolvable, not that the URL's city slug was
 * misspelled - recovery must not run, since search.js's reducer only conditionally updates
 * location/city (on a valid API location) while unconditionally updating cityUrl, so `city` here
 * can be stale leftover from an earlier, unrelated resolution rather than "unresolved slug" data.
 * @returns {string|null} Target path with trailing slash to replace with, or null to skip replace
 */
export function getSearchPageSyncTargetPath({
	pathname,
	cityUrl,
	canonical,
	city,
	fullState,
	isPathnameStale = false,
	isCoordinateSearch = false
}) {
	if (!cityUrl || !pathname.startsWith(SELF_STORAGE_PREFIX)) {
		return null;
	}

	const pathNorm = normalizePath(pathname);
	const cityUrlNorm = normalizePath(
		cityUrl.startsWith("/") ? cityUrl : `/${cityUrl}`
	);

	let targetPathNorm = cityUrlNorm;
	const canonicalPath = getPathFromCanonical(canonical);
	if (canonicalPath) {
		targetPathNorm = canonicalPath;
	}

	// Never sync TO vehicle category paths from canonical.
	// Vehicle categories are filters, not path destinations. They should only be in
	// the URL path if the user explicitly navigated to that VLP, not when filters are applied.
	const strippedTarget = stripVehicleCategoryFromPath(targetPathNorm);
	if (strippedTarget !== targetPathNorm) {
		// Canonical ends with a vehicle category. Use the stripped path instead.
		targetPathNorm = strippedTarget;
	}

	// Preserve amenity segment when current path has more segments than target
	// (user is on amenity URL but API returned city-only canonical). Exception:
	// a plain city URL (3 segments) whose API target collapsed to state-only
	// (2 segments) means the city slug was unresolved (e.g. misspelled) — recover
	// the corrected city path from the gateway's resolved location.city for
	// this request, instead of bailing out. Whatever city the API resolved is
	// trusted as-is: if the API says the city, the URL follows it.
	let isRecoveredMisspelling = false;
	if (getPathSegmentCount(pathNorm) > getPathSegmentCount(targetPathNorm)) {
		// Require the state segment to match between pathname and the (pre-recovery)
		// target, and that cityUrl/city are actually fresh for this pathname, before
		// trusting them for recovery. cityUrl/city can be stale Redux state left over
		// from a previous, unrelated page: the refetch effect only re-dispatches on a
		// location.search change (not pathname), so a pathname-only client-side
		// navigation (browser back/forward between two SPA-visited pages, or the
		// redirectTo push for a low-inventory city) can land on a brand-new pathname
		// while cityUrl/location.city still reflect the last-fetched page. Requiring
		// the state to match rules out a cross-state stale case; isPathnameStale rules
		// out a same-state one (e.g. two different Pennsylvania cities) without
		// needing cross-request state. A genuine misspelling recovery only ever
		// corrects the city segment of the pathname it was just fetched for, so both
		// checks pass for it and only it.
		const pathState = pathNorm.split("/").filter(Boolean)[1];
		const targetState = targetPathNorm.split("/").filter(Boolean)[1];
		const sameState =
			!!pathState &&
			!!targetState &&
			pathState.toLowerCase() === targetState.toLowerCase();
		// sameState only compares pathname to the API's state-only cityUrl/canonical;
		// also require the resolved location's own state to agree, so a city the
		// geocoder placed in another state can't be 301'd into this one.
		const locationStateMatches =
			!fullState ||
			slugifyCityName(fullState) === (pathState || "").toLowerCase();

		const recoveredCityUrl =
			sameState &&
			locationStateMatches &&
			!isPathnameStale &&
			!isCoordinateSearch &&
			getPathSegmentCount(pathNorm) === 3 &&
			getPathSegmentCount(targetPathNorm) === 2 &&
			buildCityUrlFromLocation({ stateUrl: targetPathNorm, city });

		if (!recoveredCityUrl) {
			return null;
		}

		targetPathNorm = normalizePath(recoveredCityUrl);
		isRecoveredMisspelling = true;
	}

	if (pathNorm === targetPathNorm) {
		return null;
	}

	// Do not overwrite URL when pathname and API target are different places (e.g. user
	// clicked back: URL is previous CLP but API state is still from the last search).
	// Skipped for a recovered misspelling: the state/city key is expected to differ
	// (that's the correction), not a stale-state mismatch.
	if (!isRecoveredMisspelling) {
		const pathKey = getStateCityKey(pathNorm);
		const targetKey = getStateCityKey(targetPathNorm);
		if (pathKey && targetKey && pathKey !== targetKey) {
			return null;
		}
	}

	return targetPathNorm.endsWith("/") ? targetPathNorm : `${targetPathNorm}/`;
}
